/**
 * Customer subscriptions management operations
 */

import { CustomerSubscription } from './types';
import { mockCustomers } from './base';

// Mock subscription data
const mockSubscriptions: Record<string, CustomerSubscription[]> = {
  'customer-uuid-1': [
    {
      id: 's1',
      product_id: '1',
      product_name: 'Email Automation API',
      plan_name: 'Professional',
      status: 'active',
      monthly_cost: 49.99,
      usage_current: 1250,
      usage_limit: 5000,
      next_billing: '2025-01-15T10:30:00Z'
    },
    {
      id: 's2',
      product_id: '2',
      product_name: 'SMS Gateway',
      plan_name: 'Starter',
      status: 'active',
      monthly_cost: 29.99,
      usage_current: 890,
      usage_limit: 2000,
      next_billing: '2025-01-15T10:30:00Z'
    }
  ],
  'customer-uuid-2': [
    {
      id: 's3',
      product_id: '1',
      product_name: 'Email Automation API',
      plan_name: 'Trial',
      status: 'trial',
      monthly_cost: 0,
      usage_current: 124,
      usage_limit: 500,
      next_billing: '2025-01-01T09:00:00Z'
    }
  ],
  'customer-uuid-3': [
    {
      id: 's4',
      product_id: '5',
      product_name: 'Weather API',
      plan_name: 'Enterprise',
      status: 'active',
      monthly_cost: 199.99,
      usage_current: 25600,
      usage_limit: 100000,
      next_billing: '2025-01-20T14:15:00Z'
    },
    {
      id: 's5',
      product_id: '3',
      product_name: 'Data Analytics Suite',
      plan_name: 'Business',
      status: 'active',
      monthly_cost: 99.99,
      usage_current: 8500,
      usage_limit: 25000,
      next_billing: '2025-01-20T14:15:00Z'
    }
  ],
  'customer-uuid-4': [
    {
      id: 's6',
      product_id: '2',
      product_name: 'SMS Gateway',
      plan_name: 'Professional',
      status: 'active',
      monthly_cost: 79.99,
      usage_current: 3200,
      usage_limit: 10000,
      next_billing: '2025-01-10T12:00:00Z'
    },
    {
      id: 's7',
      product_id: '4',
      product_name: 'File Processing API',
      plan_name: 'Starter',
      status: 'active',
      monthly_cost: 39.99,
      usage_current: 450,
      usage_limit: 1500,
      next_billing: '2025-01-10T12:00:00Z'
    }
  ],
  'customer-uuid-5': [
    {
      id: 's8',
      product_id: '1',
      product_name: 'Email Automation API',
      plan_name: 'Starter',
      status: 'suspended',
      monthly_cost: 19.99,
      usage_current: 0,
      usage_limit: 1000
    }
  ]
};

// Initialize customers with their subscriptions
mockCustomers.forEach(customer => {
  customer.individual_subscriptions = mockSubscriptions[customer.id] || [];
});

export const subscriptionOperations = {
  list: (customerId: string): { data: CustomerSubscription[] } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }
    return { data: customer.individual_subscriptions };
  },

  getById: (customerId: string, subscriptionId: string): { data: CustomerSubscription } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const subscription = customer.individual_subscriptions.find(s => s.id === subscriptionId);
    if (!subscription) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Subscription not found',
          },
        },
      };
    }

    return { data: subscription };
  },

  create: (customerId: string, data: {
    product_id: string;
    product_name: string;
    plan_name: string;
    monthly_cost: number;
    usage_limit: number;
  }): { data: CustomerSubscription } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const newSubscription: CustomerSubscription = {
      id: `s${Date.now()}`,
      product_id: data.product_id,
      product_name: data.product_name,
      plan_name: data.plan_name,
      status: 'active',
      monthly_cost: data.monthly_cost,
      usage_current: 0,
      usage_limit: data.usage_limit,
      next_billing: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
    };

    customer.individual_subscriptions.push(newSubscription);
    return { data: newSubscription };
  },

  update: (customerId: string, subscriptionId: string, data: {
    plan_name?: string;
    status?: 'active' | 'trial' | 'cancelled' | 'suspended';
    monthly_cost?: number;
    usage_limit?: number;
  }): { data: CustomerSubscription } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const subscriptionIndex = customer.individual_subscriptions.findIndex(s => s.id === subscriptionId);
    if (subscriptionIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Subscription not found',
          },
        },
      };
    }

    customer.individual_subscriptions[subscriptionIndex] = {
      ...customer.individual_subscriptions[subscriptionIndex],
      ...data
    };

    return { data: customer.individual_subscriptions[subscriptionIndex] };
  },

  cancel: (customerId: string, subscriptionId: string): { data: CustomerSubscription } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const subscriptionIndex = customer.individual_subscriptions.findIndex(s => s.id === subscriptionId);
    if (subscriptionIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Subscription not found',
          },
        },
      };
    }

    customer.individual_subscriptions[subscriptionIndex].status = 'cancelled';
    customer.individual_subscriptions[subscriptionIndex].next_billing = undefined;

    return { data: customer.individual_subscriptions[subscriptionIndex] };
  },

  reactivate: (customerId: string, subscriptionId: string): { data: CustomerSubscription } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const subscriptionIndex = customer.individual_subscriptions.findIndex(s => s.id === subscriptionId);
    if (subscriptionIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Subscription not found',
          },
        },
      };
    }

    customer.individual_subscriptions[subscriptionIndex].status = 'active';
    customer.individual_subscriptions[subscriptionIndex].next_billing = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    return { data: customer.individual_subscriptions[subscriptionIndex] };
  },

  delete: (customerId: string, subscriptionId: string): { message: string } => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }

    const subscriptionIndex = customer.individual_subscriptions.findIndex(s => s.id === subscriptionId);
    if (subscriptionIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Subscription not found',
          },
        },
      };
    }

    customer.individual_subscriptions.splice(subscriptionIndex, 1);
    return { message: 'Subscription deleted successfully' };
  }
};
